import RefreshIcon from '@mui/icons-material/Refresh';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getReporter } from '@services/monitoring';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { type WithTranslation, withTranslation } from 'react-i18next';

interface Props extends WithTranslation {
  children: ReactNode;
  /** Tag forwarded to the reporter so dashboards can group by feature. */
  feature?: string | undefined;
}

interface State {
  error: Error | null;
}

/**
 * Feature-scoped error boundary. Use this around route subtrees so a single
 * crashing screen doesn't replace the whole shell with the root fallback.
 *
 * Differences from `<RootErrorBoundary>`:
 *   - Renders an inline alert, not a full-page takeover.
 *   - Resets automatically when the consumer remounts (e.g. on route change
 *     React's reconciler swaps it out, which clears the error state).
 *   - Reports to the monitoring reporter with a `feature` tag.
 */
class FeatureBoundaryInner extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    getReporter().captureError(error, {
      componentStack: info.componentStack,
      feature: this.props.feature,
    });
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  private readonly reload = (): void => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error) {
      console.error('[FeatureBoundary]', error);
    }
    const { t } = this.props;
    if (!error) return this.props.children;

    // Stale chunks (after a Vite re-optimize in dev or a deploy in prod)
    // can't be recovered by re-rendering — they need a full reload to pick
    // up the new asset URLs. Surface that as the primary action.
    const stale = isChunkLoadError(error);

    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error" variant="outlined">
          <AlertTitle>{stale ? t('errors.appUpdated') : t('errors.featureCrashed')}</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {stale ? t('errors.appUpdatedHint') : t('errors.featureCrashedHint')}
          </Typography>
          {import.meta.env.DEV && (
            <Box
              component="pre"
              sx={{
                m: 0,
                mt: 1,
                p: 1.5,
                borderRadius: 1,
                fontSize: 12,
                overflow: 'auto',
                bgcolor: 'action.hover',
              }}
            >
              {error.stack ?? error.message}
            </Box>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            {stale ? (
              <Button
                startIcon={<RefreshIcon />}
                onClick={this.reload}
                variant="contained"
                size="small"
              >
                {t('common.reload')}
              </Button>
            ) : (
              <Button
                startIcon={<RefreshIcon />}
                onClick={this.reset}
                variant="contained"
                size="small"
              >
                {t('common.retry')}
              </Button>
            )}
          </Stack>
        </Alert>
      </Box>
    );
  }
}

function isChunkLoadError(err: Error): boolean {
  const message = err.message;
  return (
    err.name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Loading chunk \d+ failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

export const FeatureBoundary = withTranslation()(FeatureBoundaryInner);
